// Rate pacer for query permitting.
//
// The leaky bucket as a meter (GCRA): it meters the grant rate against a target,
// returning how long the caller should wait rather than rejecting.
// See https://en.wikipedia.org/wiki/Leaky_bucket.

import {QUERY_PERMIT_SOFT_THROTTLE_RPM, QUERY_PERMIT_SOFT_THROTTLE_WINDOW_MS} from './protocol';

const MS_PER_MINUTE = 60_000;

export interface SoftThrottleOptions {
  thresholdRpm?: number;
  windowMs?: number;
}

export interface Backoff {
  // ms the caller should sleep before granting
  waitMs: number;
  // wait-depth level: 0 when not throttled, else the doubling bucket of waitMs relative to one
  // interval. Consumed only by SoftThrottleMetric.
  level: number;
}

export class QueryPermitSoftThrottle {
  // Virtual time the schedule is booked through; see the file header.
  private scheduledThrough = 0;
  // Emission interval: 60000 / rpm. Sets the sustained rate.
  private declare readonly intervalMs: number;
  // Bucket depth: how far the schedule may run ahead of now before a wait is imposed.
  private declare readonly burstToleranceMs: number;

  constructor(opts: SoftThrottleOptions = {}) {
    const thresholdRpm = opts.thresholdRpm ?? QUERY_PERMIT_SOFT_THROTTLE_RPM;
    const windowMs = opts.windowMs ?? QUERY_PERMIT_SOFT_THROTTLE_WINDOW_MS;
    this.intervalMs = MS_PER_MINUTE / thresholdRpm;
    this.burstToleranceMs = Math.max(0, windowMs - this.intervalMs);
  }

  /**
   * Meters the next grant by advancing the schedule one interval. Returns the {@link Backoff}: the
   * ms to wait before granting (0 within the burst tolerance) and its wait-depth level. If the
   * grant is abandoned before delivery, pair with {@link release} to hand the interval back.
   */
  reserve(now: number): Backoff {
    const scheduleStart = Math.max(this.scheduledThrough, now);
    const allowAt = scheduleStart - this.burstToleranceMs;
    const waitMs = Math.max(0, allowAt - now);
    this.scheduledThrough = scheduleStart + this.intervalMs;
    return {waitMs, level: this.levelFor(waitMs)};
  }

  // Returns one interval to the schedule when a reserved grant is abandoned before delivery, so it
  // stops delaying later grants. One interval per abandonment keeps the schedule accurate.
  release(now: number): void {
    this.scheduledThrough = Math.max(now, this.scheduledThrough - this.intervalMs);
  }

  // Doubling bucket of the wait relative to one interval: 1 at <= interval, 2 at <= 2x, and so on.
  private levelFor(waitMs: number): number {
    if (waitMs <= 0) {
      return 0;
    }
    let level = 1;
    let step = this.intervalMs;
    while (step < waitMs) {
      step *= 2;
      level++;
    }
    return level;
  }
}
